<template lang="pug">
  #unanswered-questions
    WtTile
    .columns.is-centered.is-mobile(v-if="!memberLoaded")
      ContentLoader(:width="300" :height="100")
        rect(x="0", y="0", rx="4", ry="4", width="100%", height="40")
        rect(x="0", y="45", rx="4", ry="4", width="100%", height="40")
        rect(x="0", y="90", rx="4", ry="4", width="100%", height="40")
    .questions-unanswered-container(v-else-if="!noQuestionsLeft")
      .questions-container
        div.question-item(v-for="q in unansweredQuestions" :key="q.id")
          .question-text-container
            WtLabel.question-label(
              slot="label"
              :text="q.question"
              :required="q.required"
            )
            .points-tag(
              v-if="q.pointValue"
            )
              .point-value {{q.pointValue}}
              .point-units pts

          .question-description(v-if="q.description" v-html="q.description")
          QuestionAnswers(:question="q" @onAnswer="collectAnswer($event)" :useCustomQAStyles="true")
      WtButton(
         type="button"
          :loading="savingQuestions"
          :disabled="savingQuestions || noChangesMade"
          @click.native="answerQuestions"
      ) Submit


    .columns.is-centered.is-mobile(v-else)
      WtTitle There are no new questions to answer at the moment!
</template>

<script>
import WtTile from "@@/WtTile/WtTile";
import QuestionAnswers from "@@/ProgramQuestions/QuestionAnswers";
import { ContentLoader } from "vue-content-loader";
import VclForm from "@@/VclRegForm/VclRegForm";
import WtLabel from "@@/WtLabel/WtLabel";
import WtButton from "@@/WtButton/WtButton";
import { mapGetters, mapActions } from "vuex";
import { dateUtilityMixin } from "@/mixins/dateUtilityMixin";
import analyticsTrackingMixin from "@/mixins/analyticsTrackingMixin";
import WtTitle from "@@/WtTitle/WtTitle";

export default {
  name: "MemberQuestionsUnanswered",

  components: {
    WtTitle,
    WtTile,
    QuestionAnswers,
    ContentLoader,
    VclForm,
    WtLabel,
    WtButton
  },

  mixins: [analyticsTrackingMixin, dateUtilityMixin],

  props: {},

  data() {
    return {
      formDataQuestions: [],
      savingQuestions: false
    };
  },

  computed: {
    ...mapGetters("members", ["member", "memberLoaded"]),

    noQuestionsLeft() {
      return !(
        this.member.unansweredQuestions &&
        this.member.unansweredQuestions.length
      );
    },

    noChangesMade() {
      const validAnswers = this.formDataQuestions.filter(question => {
        if (typeof question.answer === "string") {
          const answer = question.answer.replace(/\s+/g, "");
          return answer !== "";
        } else {
          const answer = question.answer;
          return answer;
        }
      });
      return !validAnswers.length;
    },

    unansweredQuestions() {
      return this.member.unansweredQuestions;
    }
  },

  mounted() {
    this.trackFeatureView("learn", "learn_questions_unanswered");
  },

  methods: {
    ...mapActions("program", ["saveUserAnswers"]),
    ...mapActions("members", ["updateMemberValue", "updateMemberCopy"]),
    ...mapActions(["snackBar"]),

    collectAnswer(event) {
      const questionResult = {
        id: event.questionId,
        answer: this._.isArray(event.answer)
          ? event.answer.join(",")
          : event.answer
      };

      const arrayQuestionResult = this._.find(this.formDataQuestions, {
        id: questionResult.id
      });

      if (arrayQuestionResult) {
        arrayQuestionResult.answer = questionResult.answer;
      } else {
        this.formDataQuestions.push(questionResult);
      }
    },

    answerQuestions: async function() {
      this.savingQuestions = true;
      try {
        const validAnswers = this.formDataQuestions.filter(question => {
          if (typeof question.answer === "string") {
            const answer = question.answer.replace(/\s+/g, "");
            return answer !== "";
          } else {
            const answer = question.answer;
            return answer;
          }
        });
        await this.saveUserAnswers({
          programId: this.$auth.user().programId,
          memberId: this.$auth.user().id,
          questionAnswers: validAnswers
        });
        const answeredQuestions = this._.clone(this.member.answeredQuestions);
        const unansweredQuestions = this._.clone(
          this.member.unansweredQuestions
        );
        validAnswers.forEach(answeredQuestion => {
          // add question to answered questions
          const answerCopy = unansweredQuestions.find(
            q => q.id === answeredQuestion.id
          );
          answerCopy.memberAnswered = true;
          answerCopy.memberAnswer = {
            date: this.today("DATE_TIME_FULL").formatted,
            response: answeredQuestion.answer
          };
          answeredQuestions.push(answerCopy);

          // remove from unanswered questions
          const questionIndex = unansweredQuestions.findIndex(
            q => q.id === answeredQuestion.id
          );
          unansweredQuestions.splice(questionIndex, 1);
        });

        //update vuex store
        this.updateMemberValue({
          key: "answeredQuestions",
          value: answeredQuestions
        });
        this.updateMemberValue({
          key: "unansweredQuestions",
          value: unansweredQuestions
        });

        this.updateMemberCopy({
          key: "answeredQuestions",
          value: answeredQuestions
        });
        this.updateMemberCopy({
          key: "unansweredQuestions",
          value: unansweredQuestions
        });
        this.snackBar({
          duration: 10000,
          queue: false,
          message: "Your answers have been submitted!",
          type: "is-success"
        });
        this.formDataQuestions = [];
        this.savingQuestions = false;
        this.$auth.fetch();
      } catch (e) {
        this.savingQuestions = false;
      }
    }
  }
};
</script>

<style lang="sass" scoped>
#unanswered-questions
  padding-right: 25px
  padding-left: 25px
  padding-bottom: 25px
  .questions-unanswered-container
    display: flex
    gap: 20px
    flex-direction: column
  .question-text-container
    display: flex
    gap: 10px
  .question-description /deep/ a
    color: $primary
  .points-tag
    background-color: #78C98B
    display: flex
    flex-direction: row
    gap: 2px
    padding: 3px
    border-radius: 6px
    align-items: center
    justify-content: center
    width: 43px
    height: 20px
  .point-value,
  .point-units
    color: white
    font-weight: 700
    font-size: 10px
    letter-spacing: 0px
    line-height: 18px
  .questions-container
    display: flex
    flex-direction: column
    gap: 18px
    padding-left: 16px
    padding-right: 16px
  .question-item
    display: flex
    gap: 12px
    flex-direction: column
  .question-label
    font-weight: 700
    font-size: 14px
    line-height: 20px
    letter-spacing: 0px
  .question-description
    font-weight: 400
    font-size: 14px
    line-height: 20px
    letter-spacing: 0px
</style>
